import React, { useState, Fragment } from 'react';
import Input from '~~elements/Input';
import Modal from '~~elements/Modal';
import Button from '~~elements/Button';
import Select from '~~elements/Select';
import { dateTimeFriendlyTransform, dateTimeStringPattern } from '~~utils/CommonUtils';
import Multilingual from '~~utils/Multilingual';
import { SeasonResource } from '~~apis/resource';
import { SectionDiv, StyledForm } from '../styled';
import { useTournamentDispatch } from '../useTournament';
import styled from 'styled-components';
import { filter, find } from 'ramda';
import moment from 'moment';

const GroupListDiv = styled.div`
  width: 100%;
  overflow-x: auto;
  .group-items {
    min-width: 30rem;
    display: grid;
    grid-template-columns: 1fr 2fr 2fr 1fr 1fr;
    > h4 {
      white-space: nowrap;
      padding: 0 0.2rem;
    }
    > * {
      padding: 0.2rem 0;
      border-bottom: 1px solid #eee;
    }
  }
`;

const groupInitState = {
  origin_name: '',
  name: '',
  priority: '',
  next_groups: [],
  prev_groups: [],
  standings_display: false,
  is_single_elimination: false,
  scheduled_start_at: '',
  scheduled_end_at: '',
};

const GameGroup = ({ tournament, groups }) => {
  const tournamentDispatch = useTournamentDispatch();

  const [editingGroup, setEditingGroup] = useState('');
  const [editingGroupData, setEditingGroupData] = useState({ ...groupInitState });
  const [errorMsg, setErrorMsg] = useState('');

  const [tryDelete, setTryDelete] = useState(false);

  const startEditGroup = (name) => () => {
    setTryDelete(false);
    setErrorMsg('');
    if (name === 'NEW') {
      setEditingGroup(name);
      setEditingGroupData({ ...groupInitState, priority: groups.length + 1 });
      return;
    }

    const group = find((g) => g.name === name, groups);
    if (!group) {
      return;
    }

    setEditingGroup(name);
    setEditingGroupData({
      ...group,
      origin_name: name,
      priority: group.priority,
      next_groups: group.next_groups || [],
      prev_groups: group.prev_groups || [],
      scheduled_start_at: moment(group.scheduled_start_at).format('YYYY/MM/DD HH:mm'),
      scheduled_end_at: moment(group.scheduled_end_at).format('YYYY/MM/DD HH:mm'),
    });
  };

  const handleInputOnChange = (e) => {
    setErrorMsg('');

    let value = e.target.value;
    if (e.target.type === 'checkbox') {
      value = !editingGroupData[e.target.name];
    }

    setEditingGroupData({
      ...editingGroupData,
      [e.target.name]: value,
    });
  };

  const addRelationItem = (field) => () => {
    setEditingGroupData({
      ...editingGroupData,
      [field]: [...(editingGroupData[field] || []), ''],
    });
  };

  const updateRelationItem = (field, index) => (e) => {
    const value = e.target.value;
    const nextItems = [...(editingGroupData[field] || [])];
    nextItems[index] = value;
    setEditingGroupData({
      ...editingGroupData,
      [field]: nextItems,
    });
  };

  const removeRelationItem = (field, index) => () => {
    const nextItems = [...(editingGroupData[field] || [])];
    nextItems.splice(index, 1);
    setEditingGroupData({
      ...editingGroupData,
      [field]: nextItems,
    });
  };

  const getRelationOptions = (field, currentValue) => {
    const selectedValues = (editingGroupData[field] || []).filter((value) => value && value !== currentValue);
    return filter(
      (group) =>
        group.name !== editingGroupData.origin_name &&
        group.name !== editingGroupData.name &&
        !selectedValues.includes(group.name),
      groups,
    );
  };

  const handleGroupSubmit = (e) => {
    setErrorMsg('');
    if (e.preventDefault) {
      e.preventDefault();
    }

    const groupName = editingGroupData.name.trim();
    if (!groupName || ['NEW', 'NONE', 'NULL'].includes(groupName.toUpperCase())) {
      setErrorMsg(Multilingual(`TOURNAMENT.ERROR.GAME_GROUP_NAME`));
      return;
    }

    if (groupName !== editingGroupData.origin_name && find((g) => g.name === groupName, groups)) {
      setErrorMsg(Multilingual(`TOURNAMENT.ERROR.GAME_GROUP_NAME_DUPLICATE`));
      return;
    }

    const dateTimeRegExp = new RegExp(`^${dateTimeStringPattern}$`);
    const scheduledStartAt = dateTimeFriendlyTransform(editingGroupData.scheduled_start_at);
    const scheduledEndAt = dateTimeFriendlyTransform(editingGroupData.scheduled_end_at);
    if (!scheduledStartAt.match(dateTimeRegExp) || !scheduledEndAt.match(dateTimeRegExp)) {
      setErrorMsg(
        `${Multilingual(`TOURNAMENT.GAME_GROUP_ESTIMATED_DURATION`)}${Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}`,
      );
      return;
    }

    const targetMethod = editingGroup === 'NEW' ? 'createGroup' : 'updateGroup';
    SeasonResource[targetMethod]({
      seasonUniqid: tournament.uniqid,
      data: {
        ...editingGroupData,
        next_groups: editingGroupData.next_groups || [],
        prev_groups: editingGroupData.prev_groups || [],
        scheduled_start_at: scheduledStartAt,
        scheduled_end_at: scheduledEndAt,
        priority: Number(editingGroupData.priority),
        name: groupName,
      },
    })
      .then(({ data: groups }) => {
        cancelEditGroup();
        tournamentDispatch({ type: 'SET_GAME_GROUPS', payload: groups });
        refetchGames();
      })
      .catch((e) => {
        console.error(e);
        setErrorMsg(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`));
      });
  };

  const handleGroupDelete = () => {
    if (!tryDelete) {
      setTryDelete(true);
      return;
    }

    SeasonResource.deleteGroup({ seasonUniqid: tournament.uniqid, groupName: editingGroup })
      .then(({ data: groups }) => {
        cancelEditGroup();
        tournamentDispatch({ type: 'SET_GAME_GROUPS', payload: groups });
        refetchGames();
      })
      .catch((e) => {
        console.error(e);
        setErrorMsg(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`));
      });
  };

  // 分類更新或刪除時，比賽的分類會因此調整
  const refetchGames = () => {
    SeasonResource.getGames({ seasonUniqid: tournament.uniqid })
      .then(({ data: games }) => {
        tournamentDispatch({ type: 'SET_GAMES', payload: games });
      })
      .catch((e) => {
        window.location.reload();
      });
  };

  const cancelEditGroup = () => {
    setEditingGroup('');
    setEditingGroupData({ ...groupInitState });
  };

  return (
    <SectionDiv>
      <GroupListDiv>
        <div className="group-items">
          <h4>
            <Button width="unset" mini nowrap color="red" onClick={startEditGroup('NEW')}>
              {Multilingual(`COMMON.CREATE`)}
            </Button>
          </h4>
          <h4>{Multilingual(`TOURNAMENT.GAME_GROUP_NAME`)}</h4>
          <h4>{Multilingual(`TOURNAMENT.GAME_GROUP_ESTIMATED_DURATION`)}</h4>
          <h4>{Multilingual(`TOURNAMENT.GAME_GROUP_SINGLE_ELIMINATION`)}</h4>
          <h4>{Multilingual(`TOURNAMENT.GAME_GROUP_STANDINGS_DISPLAY`)}</h4>
          {groups
            .slice(0)
            .reverse()
            .map((group) => (
              <Fragment key={group.name}>
                <label>
                  <Button width="unset" mini nowrap color="blue" onClick={startEditGroup(group.name)}>
                    {Multilingual(`COMMON.EDIT`)}
                  </Button>
                </label>
                <strong>{group.name}</strong>
                <label>
                  {moment(group.scheduled_start_at).format('MM/DD')}
                  {` - `}
                  {moment(group.scheduled_end_at).format('MM/DD')}
                </label>
                <label>{group.is_single_elimination ? 'V' : 'X'}</label>
                <label>{group.standings_display ? 'V' : 'X'}</label>
              </Fragment>
            ))}
        </div>
      </GroupListDiv>
      <Modal.Modal size="lg" isOpen={!!editingGroup}>
        <Modal.ModalHeader>
          {Multilingual(`COMMON.${editingGroup === 'NEW' ? 'CREATE' : 'EDIT'}`)}
          {editingGroup === 'NEW' ? '' : ` ${editingGroup} `}
          {Multilingual(`TOURNAMENT.GAME_GROUP`)}
        </Modal.ModalHeader>
        <Modal.ModalBody>
          <StyledForm onSubmit={handleGroupSubmit}>
            <div className="box">
              {/* 分類名稱 */}
              <label>{Multilingual('TOURNAMENT.GAME_GROUP_NAME')}</label>
              <div className="input-field">
                <div className="input-box">
                  <Input
                    width="100%"
                    placeholder={Multilingual('TOURNAMENT.GAME_GROUP_NAME')}
                    name="name"
                    value={editingGroupData.name}
                    onChange={handleInputOnChange}
                  />
                </div>
              </div>
              {/* 分類順位 */}
              <label>{Multilingual('TOURNAMENT.GAME_GROUP_PRIORITY')}</label>
              <div className="input-field">
                <div className="input-box">
                  <Input
                    width="100%"
                    type="number"
                    min="1"
                    max={groups.length + (editingGroup === 'NEW' ? 1 : 0)}
                    name="priority"
                    value={editingGroupData.priority}
                    onChange={handleInputOnChange}
                  />
                </div>
              </div>
              {/* 晉級前一階段 */}
              <label>
                {Multilingual('TOURNAMENT.GAME_GROUP_PREV_GROUPS')}
                <Button width="unset" mini color="blue" onClick={addRelationItem('prev_groups')}>
                  {Multilingual(`COMMON.ADD`)}
                </Button>
              </label>
              <div className="input-field">
                <div
                  className="input-box"
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' }}
                >
                  {(editingGroupData.prev_groups || []).map((value, index) => (
                    <div key={`prev-${index}`} style={{ display: 'flex', gap: '0.5rem' }}>
                      <Select
                        width="100%"
                        name={`prev_groups_${index}`}
                        value={value}
                        onChange={updateRelationItem('prev_groups', index)}
                      >
                        <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
                        {getRelationOptions('prev_groups', value).map((group) => (
                          <option key={group.name} value={group.name}>
                            {group.name}
                          </option>
                        ))}
                      </Select>
                      <Button width="unset" mini color="transparent" onClick={removeRelationItem('prev_groups', index)}>
                        {Multilingual(`COMMON.REMOVE`)}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              {/* 晉級後一階段 */}
              <label>
                {Multilingual('TOURNAMENT.GAME_GROUP_NEXT_GROUPS')}
                <Button width="unset" mini color="blue" onClick={addRelationItem('next_groups')}>
                  {Multilingual(`COMMON.ADD`)}
                </Button>
              </label>
              <div className="input-field">
                <div
                  className="input-box"
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' }}
                >
                  {(editingGroupData.next_groups || []).map((value, index) => (
                    <div key={`next-${index}`} style={{ display: 'flex', gap: '0.5rem' }}>
                      <Select
                        width="100%"
                        name={`next_groups_${index}`}
                        value={value}
                        onChange={updateRelationItem('next_groups', index)}
                      >
                        <option value="">{Multilingual(`FORM.PLEASE_SELECT`)}</option>
                        {getRelationOptions('next_groups', value).map((group) => (
                          <option key={group.name} value={group.name}>
                            {group.name}
                          </option>
                        ))}
                      </Select>
                      <Button width="unset" mini color="transparent" onClick={removeRelationItem('next_groups', index)}>
                        {Multilingual(`COMMON.REMOVE`)}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              {/* 分類比賽時間 */}
              <label>{Multilingual('TOURNAMENT.GAME_GROUP_ESTIMATED_DURATION')}</label>
              <div className="input-field --multi-input">
                <div className="input-box">
                  <Input
                    width="100%"
                    type="text"
                    name="scheduled_start_at"
                    placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                    value={editingGroupData.scheduled_start_at}
                    onChange={handleInputOnChange}
                  />
                  {` ~ `}
                  <Input
                    width="100%"
                    type="text"
                    name="scheduled_end_at"
                    placeholder={Multilingual(`TOURNAMENT.GAME_DATE_TIME_HINT`)}
                    value={editingGroupData.scheduled_end_at}
                    onChange={handleInputOnChange}
                  />
                </div>
              </div>
              {/* 單敗淘汰 */}
              <label>
                <input
                  type="checkbox"
                  name="is_single_elimination"
                  checked={editingGroupData.is_single_elimination}
                  onChange={handleInputOnChange}
                />
                {Multilingual('TOURNAMENT.GAME_GROUP_SINGLE_ELIMINATION')}
              </label>
              <div className="input-field"></div>
              {/* 顯示戰績排行 */}
              <label>
                <input
                  type="checkbox"
                  name="standings_display"
                  checked={editingGroupData.standings_display}
                  onChange={handleInputOnChange}
                />
                {Multilingual('TOURNAMENT.GAME_GROUP_STANDINGS_DISPLAY')}
              </label>
              <div className="input-field"></div>
              <div className="error-msg">{errorMsg}</div>
            </div>
          </StyledForm>
        </Modal.ModalBody>
        <Modal.ModalFooter>
          <Button width="unset" nowrap onClick={handleGroupSubmit}>
            {Multilingual(`COMMON.${editingGroup === 'NEW' ? 'CREATE' : 'UPDATE'}`)}
          </Button>
          <Button color="transparentWithBorder" width="unset" nowrap onClick={cancelEditGroup}>
            {Multilingual(`COMMON.CANCEL`)}
          </Button>
          {editingGroup !== 'NEW' && (
            <Button
              width="unset"
              mini={!tryDelete}
              nowrap
              color={!tryDelete ? 'transparent' : 'red'}
              onClick={handleGroupDelete}
            >
              {Multilingual(`COMMON.DELETE${!tryDelete ? '' : '_CONFIRM'}`)}
            </Button>
          )}
        </Modal.ModalFooter>
      </Modal.Modal>
    </SectionDiv>
  );
};

export default GameGroup;
